import React, { useState, useEffect } from "react";
import {
  Save,
  Clock,
  MapPin,
  Loader2,
  RotateCcw,
  CheckCircle2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "../api/axiosInstance";

const MonthlyAttendanceCalendar = () => {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [attendanceData, setAttendanceData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [calendarDays, setCalendarDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bulk Tools State
  const [bulkSite, setBulkSite] = useState("");
  const [bulkValue, setBulkValue] = useState(1);

  useEffect(() => {
    const fetchMeta = async () => {
      const [empRes, projRes] = await Promise.all([
        api.get("get_employees.php"),
        api.get("get_projects.php"),
      ]);
      setEmployees(empRes.data.employees || []);
      setProjects(projRes.data.projects || []);
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    if (selectedEmpId && selectedMonth) {
      loadMonthlyLog();
    }
  }, [selectedEmpId, selectedMonth]);

  const loadMonthlyLog = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split("-").map(Number);
      const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 0 (Sun) to 6 (Sat)
      const daysInMonth = new Date(year, month, 0).getDate();

      // Create empty padding for days before the 1st of the month
      const blanks = Array(firstDayOfMonth).fill(null);
      const days = Array.from(
        { length: daysInMonth },
        (_, i) => `${selectedMonth}-${(i + 1).toString().padStart(2, "0")}`,
      );

      setCalendarDays([...blanks, ...days]);

      const res = await api.get(
        `get_employee_month_attendance.php?emp_id=${selectedEmpId}&month=${selectedMonth}`,
      );
      const mapped = {};
      res.data.attendance?.forEach((a) => {
        mapped[a.date] = {
          status: a.status,
          multiplier: parseFloat(a.multiplier) || 0,
          in_time: a.in_time || "",
          out_time: a.out_time || "",
          project_id: a.project_id,
        };
      });

      setAttendanceData(mapped);
      setOriginalData(JSON.parse(JSON.stringify(mapped)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateDayEntry = (date, field, value) => {
    const emp = employees.find((e) => e.id == selectedEmpId);
    setAttendanceData((prev) => ({
      ...prev,
      [date]: {
        ...(prev[date] || {
          status: "Present",
          multiplier: 1,
          project_id: emp?.assigned_site_id,
        }),
        [field]: value,
      },
    }));
  };

  const fillMonth = () => {
    if (!bulkSite) return alert("Select a site first");
    const newBatch = {};
    calendarDays
      .filter((d) => d !== null)
      .forEach((date) => {
        if (new Date(date).getDay() !== 0) {
          // Skip Sundays
          newBatch[date] = {
            project_id: bulkSite,
            multiplier: parseFloat(bulkValue),
            status: bulkValue > 0 ? "Present" : "Absent",
            in_time: "09:00:00",
            out_time: "18:00:00",
          };
        }
      });
    setAttendanceData((prev) => ({ ...prev, ...newBatch }));
  };

  const revertChanges = () => {
    if (window.confirm("Revert all unsaved changes?")) {
      setAttendanceData(JSON.parse(JSON.stringify(originalData)));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const emp = employees.find((e) => e.id == selectedEmpId);
      const payload = calendarDays
        .filter((d) => d !== null)
        .map((date) => ({
          date,
          employee_id: selectedEmpId,
          applied_rate: emp.rate,
          ...(attendanceData[date] || {
            status: "Absent",
            multiplier: 0,
            project_id: emp.assigned_site_id,
          }),
        }));

      await api.post("save_bulk_attendance_monthly.php", {
        attendance: payload,
      });
      alert("Calendar Saved Successfully!");
      setOriginalData(JSON.parse(JSON.stringify(attendanceData)));
    } catch (err) {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const currentEmp = employees.find((e) => e.id == selectedEmpId);

  return (
    <div className="space-y-6 p-4 max-w-[1600px] mx-auto">
      {/* Header Area */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 italic uppercase tracking-tighter flex items-center gap-3">
            <CalendarIcon className="text-indigo-600" size={32} /> Visual Ledger
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.3em] mt-1">
            Calendar Attendance Console
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 bg-white p-2 rounded-[24px] border border-slate-100 shadow-sm">
          <select
            className="p-3 bg-slate-50 border-none rounded-xl font-black text-[10px] uppercase outline-none w-64"
            value={selectedEmpId}
            onChange={(e) => setSelectedEmpId(e.target.value)}
          >
            <option value="">Choose Employee...</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} ({e.pay_type})
              </option>
            ))}
          </select>

          <input
            type="month"
            className="p-3 bg-slate-50 border-none rounded-xl font-black text-[10px] outline-none"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />

          <button
            onClick={handleSave}
            disabled={!selectedEmpId || saving}
            className="bg-slate-900 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase shadow-lg hover:bg-indigo-600 transition-all flex items-center gap-2"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}{" "}
            Save Logs
          </button>
        </div>
      </div>

      {selectedEmpId && (
        <>
          {/* Bulk Tools Overlay */}
          <div className="bg-indigo-600 p-4 rounded-[24px] flex flex-wrap items-center gap-6 text-white shadow-xl shadow-indigo-200/50">
            <div className="flex items-center gap-3 border-r border-indigo-400/50 pr-6">
              <MapPin size={20} />
              <select
                className="bg-transparent font-black text-xs uppercase outline-none"
                value={bulkSite}
                onChange={(e) => setBulkSite(e.target.value)}
              >
                <option value="" className="text-slate-900">
                  Select Bulk Site...
                </option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id} className="text-slate-900">
                    {p.project_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3 border-r border-indigo-400/50 pr-6">
              <CheckCircle2 size={20} />
              <select
                className="bg-transparent font-black text-xs uppercase outline-none"
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
              >
                {currentEmp.pay_type === "Daily"
                  ? [0, 0.5, 1, 1.2, 1.5, 2].map((v) => (
                      <option key={v} value={v} className="text-slate-900">
                        {v} Shift
                      </option>
                    ))
                  : [
                      <option key="1" value="1" className="text-slate-900">
                        Present (P)
                      </option>,
                      <option key="0.5" value="0.5" className="text-slate-900">
                        Half-Day (HD)
                      </option>,
                      <option key="0" value="0" className="text-slate-900">
                        Absent (A)
                      </option>,
                    ]}
              </select>
            </div>
            <button
              onClick={fillMonth}
              className="bg-white text-indigo-600 px-6 py-2 rounded-xl font-black text-[10px] uppercase hover:bg-slate-900 hover:text-white transition-all"
            >
              Fill Month
            </button>
            <button
              onClick={revertChanges}
              className="ml-auto text-indigo-200 hover:text-white flex items-center gap-1 font-black text-[10px] uppercase"
            >
              <RotateCcw size={14} /> Revert All
            </button>
          </div>

          {/* CALENDAR GRID */}
          <div className="grid grid-cols-7 gap-4 bg-slate-50 p-4 rounded-[40px] border border-slate-100 shadow-inner">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div
                key={d}
                className="text-center font-black text-[10px] uppercase text-slate-400 py-2"
              >
                {d}
              </div>
            ))}

            {calendarDays.map((date, idx) => {
              if (!date)
                return (
                  <div
                    key={`blank-${idx}`}
                    className="h-40 rounded-[32px] bg-slate-100/30"
                  ></div>
                );

              const data = attendanceData[date] || {
                status: "Absent",
                multiplier: 0,
                project_id: "",
              };
              const isSunday = new Date(date).getDay() === 0;
              const dayNum = date.split("-")[2];

              return (
                <div
                  key={date}
                  className={`h-auto min-h-[160px] p-4 rounded-[32px] border transition-all flex flex-col justify-between ${
                    data.multiplier > 0
                      ? "bg-white border-white shadow-md"
                      : "bg-slate-100/50 border-transparent text-slate-400"
                  } ${isSunday ? "border-rose-100 bg-rose-50/30" : ""}`}
                >
                  <div className="flex justify-between items-start">
                    <span
                      className={`text-xl font-black italic ${isSunday ? "text-rose-400" : "text-slate-900"}`}
                    >
                      {dayNum}
                    </span>
                    {data.multiplier > 0 && (
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    )}
                  </div>

                  <div className="space-y-3 my-4">
                    {/* Site Selector per day */}
                    <select
                      className="w-full bg-slate-50 p-1.5 rounded-lg text-[8px] font-black uppercase outline-none"
                      value={data.project_id || ""}
                      onChange={(e) =>
                        updateDayEntry(date, "project_id", e.target.value)
                      }
                    >
                      <option value="">No Site</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.project_name}
                        </option>
                      ))}
                    </select>

                    {/* Input based on Pay Type */}
                    {currentEmp.pay_type === "Daily" && (
                      <select
                        className={`w-full p-2 rounded-xl text-[10px] font-black uppercase outline-none ${data.multiplier > 0 ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"}`}
                        value={Number(data.multiplier)}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          updateDayEntry(date, "multiplier", val);
                          updateDayEntry(
                            date,
                            "status",
                            val > 0 ? "Present" : "Absent",
                          );
                        }}
                      >
                        {[0, 0.5, 1, 1.2, 1.5, 2].map((v) => (
                          <option key={v} value={v}>
                            {v === 0 ? "ABSENT" : `${v} SHIFT`}
                          </option>
                        ))}
                      </select>
                    )}

                    {currentEmp.pay_type === "Monthly" && (
                      <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                        {[
                          { l: "P", m: 1, s: "Present" },
                          { l: "HD", m: 0.5, s: "Present" },
                          { l: "A", m: 0, s: "Absent" },
                        ].map((opt) => (
                          <button
                            key={opt.l}
                            onClick={() => {
                              updateDayEntry(date, "multiplier", opt.m);
                              updateDayEntry(date, "status", opt.s);
                            }}
                            className={`flex-1 py-1 rounded-lg font-black text-[9px] ${data.multiplier === opt.m ? "bg-indigo-600 text-white shadow-sm" : "text-slate-400"}`}
                          >
                            {opt.l}
                          </button>
                        ))}
                      </div>
                    )}

                    {currentEmp.pay_type === "Hourly" && (
                      <div className="space-y-1">
                        <input
                          type="time"
                          className="w-full p-1 bg-slate-50 rounded text-[9px] font-bold"
                          value={data.in_time || ""}
                          onChange={(e) =>
                            updateDayEntry(date, "in_time", e.target.value)
                          }
                        />
                        <input
                          type="time"
                          className="w-full p-1 bg-slate-50 rounded text-[9px] font-bold"
                          value={data.out_time || ""}
                          onChange={(e) =>
                            updateDayEntry(date, "out_time", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>

                  {data.multiplier > 0 && currentEmp.pay_type !== "Hourly" && (
                    <p className="text-[9px] font-black text-indigo-600 text-right italic">
                      ₹{(currentEmp.rate * data.multiplier).toLocaleString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!selectedEmpId && (
        <div className="h-96 bg-white rounded-[48px] border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-300">
          <CalendarIcon size={64} className="mb-4 opacity-10" />
          <p className="font-black italic uppercase tracking-widest">
            Select an employee to visualize the monthly ledger
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyAttendanceCalendar;
